import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  forwardRef,
  HostBinding,
  inject,
} from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { logger } from '@fmnts/common/log';
import { FmntsRatingModule } from '@fmnts/components/rating';
import { isNotNullish, Option } from '@fmnts/core';
import {
  AbstractDeclarativeInputFieldComponent,
  ErrorMessagePipe,
  FormSelectField,
} from '@fmnts/declarative-forms';
import { I18nModule } from '@fmnts/i18n';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faStar } from '@fortawesome/pro-solid-svg-icons';

type FieldValue = Option<number>;

/**
 * Form field component that shows an input for entering a
 * number rating.
 */
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'next-rating-field',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './rating-field.component.html',
  imports: [FmntsRatingModule, ErrorMessagePipe, I18nModule, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RatingFieldComponent),
      multi: true,
    },
  ],
})
export class RatingFieldComponent
  extends AbstractDeclarativeInputFieldComponent<FormSelectField, FieldValue>
  implements DoCheck
{
  private readonly cd = inject(ChangeDetectorRef);
  #log = logger({ name: 'ComplimentsSelectFieldComponent' });

  @HostBinding('class') componentClass = 'form-field form-field__rating';

  /** Used to perform change detection checks */
  private _wasTouched = false;

  /** Icon to use for displaying the rating */
  public icon: IconProp = faStar;

  /**
   * Current value of the field
   */
  public get value(): FieldValue {
    return this._value;
  }
  public set value(v: FieldValue) {
    this._value = v;
    this._onChange(this._value);
    this._onTouched();
  }
  private _value: FieldValue = null;

  ngDoCheck(): void {
    // Form and field are needed to perform checks
    if (!this.form || !this.field) {
      return;
    }

    // Control is needed to perform checks
    const { control } = this;
    if (!control) {
      return;
    }

    // Perform change detection checks
    // Check if `touched` changed
    if (control.touched !== this._wasTouched) {
      this._wasTouched = control.touched;
      this.cd.markForCheck();
    }
  }

  writeValue(val: unknown): void {
    this._value = val as FieldValue;
  }

  get showError(): boolean {
    return (
      this.form &&
      (isNotNullish(this._value) || this.control.touched) &&
      this.control.invalid
    );
  }

  protected override setFocusedState(isFocused: boolean): void {
    this.#log.error('setFocusedState: Method not implemented.');
  }
}
